import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {accountGetAll, accountExcel} from '../../../redux/slices/account.slice'
import {basicLookup, projectLookup} from '../../../redux/slices/lookup.slice'

import {pagination, AccountType} from '../../../helpers'

import {
  useMemoizedStaticData
} from '../../../utils/performanceOptimizations'

const selectAccountData = (state) => ({
  accountListPage: state.account.accountListPage,
  loading: state.account.loading
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  Currency: state.lookup.Currency
})

function List() {
  const intl = useIntl()
  
  const {accountListPage, loading} = useSelector(selectAccountData)
  const {projectLookupList, Currency} = useSelector(selectLookupData)
  const dispatch = useDispatch()

  const [params, setParams] = useState({
    ...pagination,
    order: 'balance',
    sort: 'desc',
    items_per_page: 15
  })

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(basicLookup('Currency'))
  }, [dispatch])

  useEffect(() => {
    dispatch(accountGetAll(params))
  }, [dispatch, params])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      accountExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.BANKCASE'}),
      })
    )
  }, [dispatch, params, intl])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'BANKCASHACCOUNT.SEARCH.NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCellDouble item={props} double='accountTypeName' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'BANKCASHACCOUNT.SEARCH.BANKBRANCH'})}
          className='min-w-125px'
        />
      ),
      accessor: 'bankBranchName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'BANKCASHACCOUNT.SEARCH.PROJECT'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'BANKCASHACCOUNT.SEARCH.IBAN'})}
          className='min-w-125px'
        />
      ),
      accessor: 'iban',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'BANKCASHACCOUNT.SEARCH.CURRENCY'})}
          className='min-w-125px'
        />
      ),
      accessor: 'currencyName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'BANKCASHACCOUNT.SEARCH.BALANCE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'balance',
      Cell: ({...props}) => <CustomCell item={props} price />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'lastModifiedBy',
      Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
    },
  ], [intl, handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'BANKCASHACCOUNT.SEARCH.ACCOUNTTYPEID',
          data: AccountType,
          name: 'accountTypeId',
        },
        {
          title: 'BANKCASHACCOUNT.SEARCH.PROJECT',
          data: projectLookupList,
          name: 'projectId',
        },
        {
          title: 'BANKCASHACCOUNT.SEARCH.CURRENCY',
          data: Currency,
          name: 'currencyId',
        },
        {
          title: 'BANKCASHACCOUNT.SEARCH.MINBALANCE',
          type: 'price',
          name: 'minBalance',
          suffix: '',
        },
        {
          title: 'BANKCASHACCOUNT.SEARCH.MAXBALANCE',
          type: 'price',
          name: 'maxBalance',
          suffix: '',
        },
      ],
    },
  ], [handleExcelExport, projectLookupList, Currency])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.BANKCASE'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
        />
        <KTCardBody>
          {!accountListPage && <ListLoading />}
          {accountListPage && (
            <Table
              items={accountListPage}
              cols={columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
